import React from 'react';
import { 
  BusinessOperationsIcon,
  GrowthScalingIcon,
  FundingIcon,
  RegulatoryIcon,
  KitchenIcon,
  TrainingIcon,
  DigitalIcon,
  CommunityIcon,
  DreamIcon,
  SupplyIcon,
  FeedbackIcon,
  GroceryShoppingIcon
} from '../../icons/FocusGroupIcons';
import { CheckCircle, Circle, RadioButtonUnchecked } from '@mui/icons-material';
import { FocusGroupSessionSchema } from '../../data/types';
import { capitalizeWords } from '../../utils/helpers';

interface SessionMapProps {
  currentSession: number;
  currentSection: number;
  sessions: FocusGroupSessionSchema[];
  isFinalSection: boolean;
}

const SessionMap: React.FC<SessionMapProps> = ({ currentSession, currentSection, sessions, isFinalSection }) => {
  const getIcon = (sessionId: string, sectionId: string) => {
    const iconMap: Record<string, any> = {
      'business-operations': BusinessOperationsIcon,
      'growth-scaling': GrowthScalingIcon,
      'funding': FundingIcon,
      'regulatory': RegulatoryIcon,
      'kitchen': KitchenIcon,
      'training': TrainingIcon,
      'digital': DigitalIcon,
      'community': CommunityIcon,
      'dream': DreamIcon,
      'marketplace': KitchenIcon,
      'supply': SupplyIcon,
      'feedback': FeedbackIcon,
      'grocery-shopping': GroceryShoppingIcon,
    };
    return iconMap[sectionId];
  };

  const getStatus = (sessionIndex: number, sectionIndex: number) => {
    if (sessionIndex < currentSession || isFinalSection) return 'completed';
    if (sessionIndex === currentSession) {
      if (sectionIndex < currentSection) return 'completed';
      if (sectionIndex === currentSection) return 'current';
    }
    return 'upcoming';
  };

  return (
    <div className="w-72 bg-white rounded-xl shadow-lg p-6 sticky top-4 border border-gray-100">
      <h3 className="text-lg font-bold text-gray-400 font-raleway mb-6 flex items-center">
        {/* <span className="w-2 h-2 bg-primaryDark-0/70 rounded-full mr-2"></span> */}
        Summary
      </h3>
      <div className="space-y-8">
        {sessions.map((session, sessionIndex) => (
          <div key={session.id} className="space-y-3">
            <div className="flex items-center space-x-3">
              {(sessionIndex < currentSession || isFinalSection) ? (
                <CheckCircle className="text-emerald-500 w-5 h-5" />
              ) : sessionIndex === currentSession ? (
                <RadioButtonUnchecked className="text-primaryDark-0 w-5 h-5 animate-pulse" />
              ) : (
                <Circle className="text-gray-300 w-5 h-5" />
              )}
              <span className={`font-semibold ${
                sessionIndex === currentSession ? 'text-primaryDark-0' : 
                (sessionIndex < currentSession || isFinalSection) ? 'text-emerald-600' : 'text-gray-500'
              }`}>
                {session.sessionType ? `${capitalizeWords(session.sessionType.split('_').join(' '))} Sections` : 'Sections'}
              </span>
            </div>
            <div className="ml-8 space-y-2">
              {session.sections.map((section, sectionIndex) => {
                const Icon = getIcon(session.id, section.id);
                const status = getStatus(sessionIndex, sectionIndex);
                
                return (
                  <div 
                    key={section.id}
                    className={`flex items-center space-x-3 p-3 rounded-lg transition-all duration-200 ${
                      status === 'current' ? 'bg-primaryDark-0/10 border border-primaryDark-0/20' :
                      status === 'completed' ? 'bg-emerald-50 border border-emerald-100' : 
                      'bg-gray-50 border border-gray-100'
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${
                      status === 'current' ? 'text-primaryDark-0' :
                      status === 'completed' ? 'text-emerald-500' : 'text-gray-400'
                    }`} />
                    <span className={`text-sm ${
                      status === 'current' ? 'text-primaryDark-0 font-medium' :
                      status === 'completed' ? 'text-emerald-600' : 'text-gray-500'
                    }`}>
                      {section.title}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SessionMap; 